class ProductSwatches extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.addEventListener('change', this.onChange.bind(this));
    });

    this.variants = JSON.parse(this.querySelector('[type="application/json"]').textContent);

    if (!this.variants) {
      console.error('No variants found');
      return;
    }

    this.productCard = this.closest('[data-product-card]');
    this.productCardImageContainer = this.productCard.querySelector('[data-product-card-image]');
    this.productCardImage = this.productCardImageContainer.querySelectorAll('img')[0];
    this.productCardLinks = this.productCard.querySelectorAll('a');
    this.productCardForm = this.productCard.querySelector('form');
    this.productCardPrice = this.productCard.querySelector('.price');
  }

  onChange(event) {
    const input = event.target;
    const value = input.value;
    const variant = this.variants.find(variant => variant.option1 === value);

    // update the image
    if (this.productCardImage) {
      this.productCardImage.src = variant.featured_image;
      this.productCardImage.srcset = '';
    }

    // update the price
    if (this.productCardPrice) {
      this.productCardPrice.textContent = variant.variant_price;
    }

    // update the url
    this.productCardLinks.forEach((link) => {
      link.href = variant.url;
      link.title = variant.title;
    });

    //check for availability
    if (variant.available) {
      this.productCard.classList.remove(...['opacity-50', 'saturate-50']);
    }
    else {
      this.productCard.classList.add(...['opacity-50', 'saturate-50']);
    }

    if (this.productCardForm) {
      this.productCardForm.querySelector('input[name="id"]').value = variant.variant_id;
    }

    window.dispatchEvent(
      new CustomEvent('product:swatch-change', {
        bubbles: true,
        detail: {
          variantId: variant.variant_id,
          productCardForm: this.productCard.querySelector('product-form-component'),
        },
      }),
    );
  }
}
if (!customElements.get('product-swatches')) {
  customElements.define('product-swatches', ProductSwatches);
}

/**
 * Swatches for metafield-linked color sibling products (custom.connected_products).
 * Hovering a swatch (hover devices) or tapping it (touch devices) updates the
 * card with the linked product's data from the embedded JSON. Price and badges
 * are server-rendered HTML fragments in that JSON, so the swapped card keeps
 * the exact snippet markup (compare-at prices, sold-out badges, etc.).
 */
class LinkedProductSwatches extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('script[type="application/json"]');
    this.products = data ? JSON.parse(data.textContent) : [];
    this.productCard = this.closest('[data-product-card]');
    if (!this.productCard || this.products.length === 0) return;

    // Tap / keyboard selection
    this.addEventListener('change', event => this.activate(event.target.value));

    // Warm the browser cache with each sibling's first image on the first
    // interaction with the card, so the image swap on hover/tap is instant
    const warmImageCache = () => this.preloadFirstImages();
    this.productCard.addEventListener('touchstart', warmImageCache, { once: true,
      passive: true });
    this.productCard.addEventListener('pointerenter', warmImageCache, { once: true });

    // Hover activation on hover-capable devices
    if (window.matchMedia('(hover: hover)').matches) {
      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        const label = this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        label?.addEventListener('pointerenter', () => {
          if (input.checked) return;
          input.checked = true;
          this.activate(input.value);
        });
      });
    }
  }

  preloadFirstImages() {
    this.products.forEach((entry) => {
      const firstImage = entry.images?.[0];
      if (firstImage) new Image().src = firstImage;
    });
  }

  activate(productId) {
    const linkedProduct = this.products.find(entry => String(entry.id) === String(productId));
    if (!linkedProduct) return;

    // Update the card images (all carousel slides on touch, primary/hover image on desktop)
    const carousel = this.productCard.querySelector('product-card-carousel');
    if (carousel) this.swapImages(carousel, linkedProduct);

    // Update the title text, keeping the heading markup of this card
    const title = this.productCard.querySelector('.product-card__information')?.firstElementChild;
    if (title) title.textContent = linkedProduct.title;

    // Update the price with the server-rendered markup (compare-at etc.)
    const price = this.productCard.querySelector('.product__price');
    if (price && linkedProduct.price_html) price.innerHTML = linkedProduct.price_html;

    // Sync the badges (sold out / sale): replace, remove or insert
    const badges = this.productCard.querySelector('.badges');
    if (badges) {
      if (linkedProduct.badges_html) badges.outerHTML = linkedProduct.badges_html;
      else badges.remove();
    }
    else if (linkedProduct.badges_html) {
      this.productCard.querySelector('.product-card__images')?.insertAdjacentHTML('beforeend', linkedProduct.badges_html);
    }

    // Swap the quick shop (form or dialog + trigger) for the linked product's
    // own, so add-to-cart adds the right product — only when this card has one
    const footer = this.productCard.querySelector('.product-card__footer');
    if (footer && linkedProduct.quick_shop_html) {
      const quickShopParts = footer.querySelectorAll('.quick-shop, .quick-shop__trigger, .quick-shop__form');
      quickShopParts.forEach((part, index) => {
        if (index === 0) part.outerHTML = linkedProduct.quick_shop_html;
        else part.remove();
      });
    }

    // Update the links
    this.productCard.querySelectorAll('a').forEach((link) => {
      link.href = linkedProduct.url;
      link.title = linkedProduct.title;
    });
  }

  /**
   * Swaps the card images only after the first image has been fetched and
   * decoded, with a loading pulse on the card image in the meantime — so the
   * old image never sits next to already-swapped text/badges unannounced.
   */
  swapImages(carousel, linkedProduct) {
    const firstImage = linkedProduct.images?.[0];
    if (!firstImage) return;

    // Guards against out-of-order loads when hovering several swatches quickly
    this.pendingImageSwap = firstImage;
    carousel.classList.add('product-card__carousel--loading');

    const preloadedImage = new Image();
    preloadedImage.src = firstImage;
    preloadedImage
      .decode()
      .catch(() => {})
      .finally(() => {
        if (this.pendingImageSwap !== firstImage) return;
        carousel.setImages?.(linkedProduct.images);
        carousel.classList.remove('product-card__carousel--loading');
      });
  }
}
if (!customElements.get('linked-product-swatches')) {
  customElements.define('linked-product-swatches', LinkedProductSwatches);
}
